// ─────────────────────────────────────────────────────────────────────────────
// The Prep Notepad's component form — transcribed from the legacy XAF
// NON-PERSISTENT detail views:
//
//   NPReagentPrepLog_DetailView_Chemistry   (Model.xafml :33423)
//   NPReagentPrepLog_DetailView_MicroMedia  (Model.xafml :33508)
//
// over `Modules.BusinessObjects.ReagentPreparation.NPReagentPrepLog`, the
// in-memory copy the wizard edits (spec §4.3 / §9).
//
// Captions are XAF's verbatim, including its stray trailing spaces
// ("Final Vol (V2) ") and the unspaced "LT#Exp Date" — those are what the
// analyst reads today.
//
// Order is the LayoutItem order, NOT the PropertyEditor declaration order: a
// PropertyEditor with no LayoutItem is not displayed at all.
// ─────────────────────────────────────────────────────────────────────────────

/** How a Notepad field renders. Only the control types these two views use. */
export type NpKind =
  | 'readonly'    // AllowEdit="False"
  | 'numeric'     // numeric-only text (spec §10.2: digits and '.' only)
  | 'text'        // free text (the Micro Y/N + control fields)
  | 'checkbox'    // Solvent
  | 'formula'     // read-only multi-line Formula
  | 'comment'     // RowCount=4 textarea
  | 'units'       // ReagentUnits dropdown (LookupProperty="Units")
  | 'approach'    // CalculationApproach dropdown (LookupProperty="Approach")
  | 'grid';       // ASPxGridLookup — VendorStock / LT / LabStock / LLT

export interface NpField {
  /** Key on the component draft. */
  key: string;
  /** XAF caption, verbatim. */
  label: string;
  kind: NpKind;
  /** For `grid`: which lookup feed + its popup columns. */
  lookup?: 'vendor-stock' | 'lt' | 'lab-stock' | 'llt';
  cols?: string[];
  /** Shown only when the matched Formula mentions this variable — XAF binds MW
   *  and EqWt to `isShowMW` / the formula text (spec §4.2: "Visible only if the
   *  Formula contains MW"). Their captions render red. */
  onlyWhenFormulaHas?: 'MW' | 'EqWt';
}

/** The popup columns of each grid-lookup, from its XAF LookupListView. Column 0
 *  is the value the closed combo shows. */
export const LOOKUP_COLS = {
  'vendor-stock': ['Item', 'Vendor', 'Description', 'Catalog#'],
  'lt':           ['LT#', 'VendorLot', 'ExpiryDate'],
  'lab-stock':    ['PrepName', 'PrepType'],
  'llt':          ['LLT', 'PrepType', 'PrepName', 'PrepDate', 'ExpiryDate'],
} as const;

/**
 * Popup geometry per grid-lookup, so EVERY column is visible without a
 * horizontal scrollbar.
 *
 * The shared popup defaults to 520px with `width:100%` nowrap cells, so a
 * four-column lookup carrying long item names ("2-Mercaptopyridine N-oxide
 * sodium salt") overflowed and hid Description and Catalog# behind a scrollbar.
 *
 * `widths` are passed as `colWidths` together with `wrapCells`, which switches
 * the table to `table-layout: fixed` — so the columns hold these proportions
 * exactly and long text WRAPS onto a second line instead of forcing the table
 * wider than its popup. `popup` is the sum plus padding; the shared CSS already
 * caps it at 94vw, so a narrow screen shrinks it rather than overflowing.
 *
 * Widths follow the proportions in spec §6.1 (Item 130 / Description 150 /
 * Vendor 130 / Item Code 100), scaled up because this popup has the room.
 */
export const LOOKUP_GEOMETRY: Record<string, { widths: number[]; popup: number }> = {
  // Item · Vendor · Description · Catalog#
  'vendor-stock': { widths: [250, 150, 260, 120], popup: 820 },
  // LT# · VendorLot · ExpiryDate
  'lt':           { widths: [110, 150, 110],          popup: 420 },
  // PrepName · PrepType
  'lab-stock':    { widths: [260, 160],               popup: 470 },
  // LLT · PrepType · PrepName · PrepDate · ExpiryDate
  'llt':          { widths: [130, 120, 220, 110, 110], popup: 740 },
};

const STOCK_FIELDS: NpField[] = [
  { key: 'componentID',  label: 'Component ID', kind: 'readonly' },
  { key: 'vendorStockId', label: 'Vendor Stock', kind: 'grid', lookup: 'vendor-stock', cols: [...LOOKUP_COLS['vendor-stock']] },
  { key: 'ltId',          label: 'LT#',          kind: 'grid', lookup: 'lt',           cols: [...LOOKUP_COLS['lt']] },
  { key: 'labStockId',    label: 'Lab Stock',    kind: 'grid', lookup: 'lab-stock',    cols: [...LOOKUP_COLS['lab-stock']] },
  { key: 'lltId',         label: 'LLT#',         kind: 'grid', lookup: 'llt',          cols: [...LOOKUP_COLS['llt']] },
];

/** Chemistry — NPReagentPrepLog_DetailView_Chemistry layout, col1 (16 items). */
export const NP_CHEM_COL1: NpField[] = [
  ...STOCK_FIELDS,
  { key: 'calculationApproachId',  label: 'Calculation Approach',        kind: 'approach' },
  { key: 'w1UnitsId',              label: 'Initial Weight Taken (W1) Units', kind: 'units' },
  { key: 'v1UnitsId',              label: 'Initial Vol Taken (V1) Units',    kind: 'units' },
  { key: 'c1UnitsId',              label: 'Stock Conc (C1) Units',       kind: 'units' },
  { key: 'v2UnitsId',              label: 'Final Vol (V2) Units',        kind: 'units' },
  { key: 'c2UnitsId',              label: 'Final Conc (C2) Units',       kind: 'units' },
  { key: 'purity',                 label: 'Purity (%)',                  kind: 'numeric' },
  { key: 'density',                label: 'Density',                     kind: 'numeric' },
  { key: 'mw',                     label: 'MW',   kind: 'numeric', onlyWhenFormulaHas: 'MW' },
  { key: 'eqWt',                   label: 'EqWt', kind: 'numeric', onlyWhenFormulaHas: 'EqWt' },
  { key: 'constant',               label: 'Constant',                    kind: 'numeric' },
];

/** Chemistry col2 (10 items). Note Formula sits ABOVE the value fields. */
export const NP_CHEM_COL2: NpField[] = [
  { key: 'solvent',        label: 'Solvent',           kind: 'checkbox' },
  { key: 'ltExpDate',      label: 'LT#Exp Date',       kind: 'readonly' },
  { key: 'lltExpDate',     label: 'LLT#Exp Date',      kind: 'readonly' },
  { key: 'formula',        label: 'Formula',           kind: 'formula' },
  { key: 'weight_g_w1',    label: 'Weight (g) (W1)',   kind: 'numeric' },
  { key: 'volumeTaken_V1', label: 'Volume Taken (V1) ', kind: 'numeric' },
  { key: 'stockConc_C1',   label: 'Stock Conc (C1) ',  kind: 'numeric' },
  { key: 'finalVol_V2',    label: 'Final Vol (V2) ',   kind: 'numeric' },
  { key: 'finalConc_C2',   label: 'Final Conc (C2)',   kind: 'numeric' },
  { key: 'comment',        label: 'Comment',           kind: 'comment' },
];

/** Micro Media col1 — the live G5 Prep Notepad (owner screenshots 2026-10-26 + 2026-11-03). No
 *  calculation approach, units, C1/C2, Formula or Constant: Micro does no
 *  calculation, and XAF never saves a Micro Formula / Constant (ChangePrepLog
 *  "Micro"), so offering them would only lose what was typed. */
export const NP_MICRO_COL1: NpField[] = [
  ...STOCK_FIELDS,
  { key: 'weight_g_w1',        label: 'Wt Taken (g)',              kind: 'numeric' },
  { key: 'volumeTaken_V1',     label: 'Volume Taken (ml)',         kind: 'numeric' },
  { key: 'finalVol_V2',        label: 'Final Volume (ml)',         kind: 'numeric' },
  // Captions exactly as the live G5 screen (owner screenshot 2026-11-03).
  { key: 'phCriteria',         label: 'PHCriteria',                kind: 'text' },
  { key: 'ph',                 label: 'PH',                        kind: 'text' },
];

/** Micro Media col2 — as the live G5 screen (owner screenshot 2026-11-03): Solvent,
 *  the two lot expiry dates and FORMULA. This REVERSES the 2026-10-26 layout, which
 *  had a Comment box and no Formula. Formula is read-only (as on Chemistry): Micro
 *  does no calculation and XAF never saves a Micro Formula, so a typed one would be
 *  lost. Comment is no longer offered here; a component's existing Comment is
 *  carried through the Notepad unchanged (it stays in its draft).
 *  The five micro check fields (Autoclave … -Control) sit at the TOP of this column,
 *  above Solvent / the exp dates / Formula, so the two columns are evenly filled
 *  (owner requests 2026-11-03: "replace those marked fields here for the 50%-50%
 *  layout", then "replace these controls" — check fields first, Formula last). */
export const NP_MICRO_COL2: NpField[] = [
  { key: 'autoclave',          label: 'Autoclave(Y/N)',            kind: 'text' },
  { key: 'filterSterilization', label: 'Filter Sterilization(Y/N)', kind: 'text' },
  { key: 'sporeGrowth',        label: 'Spore Growth(Y/N)',         kind: 'text' },
  { key: 'positiveControl',    label: '+Control',                  kind: 'text' },
  { key: 'negativeControl',    label: '-Control',                  kind: 'text' },
  { key: 'solvent',    label: 'Solvent',       kind: 'checkbox' },
  { key: 'ltExpDate',  label: 'LT#Exp Date',   kind: 'readonly' },
  { key: 'lltExpDate', label: 'LLT#Exp Date',  kind: 'readonly' },
  { key: 'formula',    label: 'Formula',       kind: 'formula' },
];

/**
 * Fields the Solvent checkbox LOCKS when ticked (spec §13).
 *
 *   Chemistry — Formula, Calculation Approach, every unit, W1, V1, C1, C2,
 *               Purity, Density, MW, EqWt, Constant and the exp dates go
 *               read-only. **Final Vol (V2) stays editable.**
 *   Micro     — the measurement and micro fields lock; §11.2.
 */
export const SOLVENT_LOCKS_CHEM = new Set([
  'calculationApproachId', 'w1UnitsId', 'v1UnitsId', 'c1UnitsId', 'v2UnitsId', 'c2UnitsId',
  'weight_g_w1', 'volumeTaken_V1', 'stockConc_C1', 'finalConc_C2',
  'purity', 'density', 'mw', 'eqWt', 'constant',
]);

export const SOLVENT_LOCKS_MICRO = new Set([
  'weight_g_w1', 'volumeTaken_V1', 'finalVol_V2',
  'phCriteria', 'ph', 'autoclave', 'filterSterilization', 'sporeGrowth',
  'positiveControl', 'negativeControl',
]);

/**
 * FIELD-STATE COLOURS for the calculation engine (spec §14.2) — the mapping to
 * use when BRANCH_D lands.
 *
 * Legacy paints three states: LightGray (not applicable, read-only),
 * LightYellow (an input the matched formula needs, editable) and Yellow (the
 * solved-for target, read-only and cleared before computing).
 *
 * This app has only TWO field colours, and they mean something slightly
 * different: editable = yellow (--ai-required-bg), read-only = grey
 * (--ai-readonly-bg), and read-only vs editable is the ONLY cue (BO UI
 * Standard, app-wide since 2026-07-10). Owner ruling 2026-09-26: "the yellow
 * back color should be shown for the fields where the user can choose values or
 * enter values".
 *
 * So the mapping is:
 *   LightGray  (not applicable)  -> grey   .sd-input.readonly
 *   LightYellow (formula input)  -> yellow .sd-input           (editable)
 *   Yellow      (formula RESULT) -> bright yellow .sd-input.readonly.rgp-calc-result
 *
 * The result keeps XAF's own bright Yellow (owner request 2026-10-23: "the field
 * that will be found should be in colour matching with the XAF"), so the three
 * states read apart at a glance: grey = not used, light yellow = type it,
 * bright yellow = calculated. It stays read-only.
 */
export const FIELD_STATE_CLASS = {
  notApplicable: 'sd-input readonly',
  formulaInput:  'sd-input',
  formulaResult: 'sd-input readonly rgp-calc-result',
} as const;

/**
 * Measurement field key → the formula TOKEN the engine reports it by.
 *
 * The server answers in the lab's own vocabulary ("V1", "Purity") because that
 * is what the analyst typed into the Calculation Editor; the form knows its
 * fields by their column names. This is the one place the two meet, so a
 * renamed field never has to be chased through the dialog.
 */
export const FIELD_FORMULA_TOKEN: Readonly<Record<string, string>> = {
  volumeTaken_V1: 'V1',
  weight_g_w1:    'W1',
  stockConc_C1:   'C1',
  finalVol_V2:    'V2',
  finalConc_C2:   'C2',
  purity:         'Purity',
  density:        'Density',
  mw:             'MW',
  eqWt:           'EqWt',
  constant:       'Constant',
};

/** The reverse map — the draft field a formula token writes to. */
export const FORMULA_TOKEN_FIELD: Readonly<Record<string, string>> =
  Object.fromEntries(Object.entries(FIELD_FORMULA_TOKEN).map(([k, v]) => [v, k]));
